// Once-per-session "What's new" modal. Renders on first visit after
// a major-flagged changelog entry has been published; the user clicks
// Got it to mark seen and dismiss.
//
// Dual gating:
//   1. /api/changelog/unread says hasMajor=true → we have something to
//      pop. (Major-flagged entries are explicitly opted-in by admin.)
//   2. sessionStorage flag — within one browser session we only show
//      the popover ONCE so a route change doesn't re-trigger it.
//
// "Got it" calls mark-seen; the unread count drops to 0 so the bell-
// dot also clears. Non-major unread entries just live as the bell-dot
// until the user navigates to /notifications (or future /whats-new
// page) and reads them.

import {
  Badge,
  Box,
  Button,
  Dialog,
  HStack,
  Portal,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FiSend } from "react-icons/fi";
import { useMe } from "@/hooks/useAuth";
import { useMarkChangelogSeen, useUnreadChangelog } from "@/hooks/useChangelog";

const SESSION_KEY = "cw_changelog_popover_shown_v1";

export default function ChangelogPopover() {
  const { data: me } = useMe();
  const { data } = useUnreadChangelog(Boolean(me));
  const markSeen = useMarkChangelogSeen();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!data?.hasMajor) return;
    if (typeof window === "undefined") return;
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      // sessionStorage unavailable — still open the popover, the
      // mark-seen on dismiss bumps the server-side pointer.
    }
    setOpen(true);
  }, [data?.hasMajor]);

  if (!open || !data || data.latest.length === 0) return null;

  function dismiss(): void {
    markSeen.mutate(undefined, {
      onSettled: () => setOpen(false),
    });
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(d) => setOpen(d.open)}
      closeOnInteractOutside={false}
      size="md"
      scrollBehavior="inside"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxH="80vh">
            <Dialog.Header>
              <HStack gap={2}>
                <Box color="purple.500">
                  <FiSend />
                </Box>
                <Dialog.Title>What&apos;s new in Callwright</Dialog.Title>
              </HStack>
            </Dialog.Header>
            <Dialog.Body>
              <Stack gap={5}>
                {data.latest.map((e) => (
                  <Box key={e.id}>
                    <HStack gap={2} mb={1}>
                      <Badge size="sm" colorPalette={e.major ? "purple" : "blue"}>
                        {e.version}
                      </Badge>
                      <Text fontSize="sm" fontWeight="semibold">
                        {e.title}
                      </Text>
                    </HStack>
                    {/* Body is markdown but we render as preserved
                        whitespace for now — full Markdown rendering is
                        a follow-up if you want headings + lists. */}
                    <Text fontSize="sm" color="fg.muted" whiteSpace="pre-wrap">
                      {e.body}
                    </Text>
                  </Box>
                ))}
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button
                colorPalette="purple"
                onClick={dismiss}
                loading={markSeen.isPending}
              >
                Got it
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
